<template>
  <div class="product-form-container">
    <!-- Header -->
    <div class="form-header">
      <div class="header-left">
        <router-link to="/configuracion/productos" class="back-link">
          <i class="fas fa-arrow-left"></i>
        </router-link>
        <div>
          <h2 class="form-title">{{ isEditMode ? 'Editar Producto' : 'Nuevo Producto' }}</h2>
          <p class="form-subtitle" v-if="isEditMode && product.name">{{ product.name }}</p>
        </div>
      </div>
      <div class="header-actions">
        <button type="button" class="btn-action btn-action-secondary" @click="resetForm">
          <i class="fas fa-undo"></i>
          <span class="d-none d-md-inline">Reiniciar</span>
        </button>
        <button 
          type="button" 
          class="btn-action btn-action-primary" 
          :disabled="isSubmitting"
          @click="handleSubmit"
        >
          <i :class="isSubmitting ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
          {{ isSubmitting ? 'Guardando...' : (isEditMode ? 'Actualizar' : 'Guardar') }}
        </button>
      </div>
    </div>

    <form @submit.prevent="handleSubmit" class="form-content">
      <!-- Section: Información Básica -->
      <section class="form-section">
        <div class="section-indicator">
          <div class="indicator-icon">
            <i class="fas fa-box"></i>
          </div>
          <div class="indicator-line"></div>
        </div>
        <div class="section-content">
          <div class="section-label">Información Básica</div>
          <div class="form-card">
            <div class="row g-3">
              <div class="col-12">
                <label class="field-label">Nombre del Producto <span class="required-dot"></span></label>
                <input v-model="product.name" type="text" class="field-input" required placeholder="Ej: Vidrio templado iPhone 15" />
              </div>
              <div class="col-md-6">
                <label class="field-label">Marca</label>
                <input v-model="product.brand" type="text" class="field-input" placeholder="Ej: Samsung" />
              </div>
              <div class="col-md-6">
                <label class="field-label">Categoría <span class="required-dot"></span></label>
                <div class="field-group">
                  <select 
                    v-model="product.category" 
                    class="field-input"
                    required
                    v-if="!showCategoryInput"
                  >
                    <option :value="null">Seleccionar categoría...</option>
                    <option 
                      v-for="cat in categories" 
                      :key="cat.id" 
                      :value="cat.id"
                    >
                      {{ cat.name }}
                    </option>
                  </select>
                  <input
                    v-if="showCategoryInput"
                    v-model="newCategoryName"
                    type="text"
                    class="field-input"
                    placeholder="Nombre de la nueva categoría"
                  />
                  <div class="field-group-actions">
                    <button 
                      type="button" 
                      class="icon-btn"
                      @click="toggleCategoryInput"
                      :title="showCategoryInput ? 'Volver a selección' : 'Crear nueva categoría'"
                    >
                      <i :class="showCategoryInput ? 'fas fa-times' : 'fas fa-plus'"></i>
                    </button>
                    <button 
                      v-if="showCategoryInput" 
                      type="button" 
                      class="icon-btn icon-btn-brand"
                      @click="createNewCategory"
                      :disabled="!newCategoryName.trim() || creatingCategory"
                    >
                      <i :class="creatingCategory ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                    </button>
                    <button 
                      v-if="!showCategoryInput && product.category" 
                      type="button" 
                      class="icon-btn icon-btn-danger"
                      @click="confirmDeleteCategory"
                      :disabled="deletingCategory"
                      title="Eliminar categoría"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <label class="field-label">SKU</label>
                <div class="field-group">
                  <input v-model="product.sku" type="text" class="field-input" placeholder="Ej: FERT-NUTRI-A1B2" />
                  <button
                    type="button"
                    class="icon-btn"
                    @click="generateBaseSku"
                    :disabled="generatingBaseSku"
                    title="Generar SKU automático"
                    style="border-radius: 0 5px 5px 0;"
                  >
                    <i :class="generatingBaseSku ? 'fas fa-spinner fa-spin' : 'fas fa-magic'"></i>
                  </button>
                </div>
                <span class="field-hint">Se genera automáticamente si se deja vacío</span>
              </div>
              <div class="col-md-3">
                <label class="field-label">Volumen</label>
                <input v-model="product.volume" type="text" class="field-input" placeholder="250ml, 1L" />
              </div>
              <div class="col-md-3">
                <label class="field-label">Dimensiones</label>
                <input v-model="product.dimensions" type="text" class="field-input" placeholder="10x15x20cm" />
              </div>
              <div class="col-12">
                <div class="shipping-section-label">
                  <i class="fas fa-truck me-1"></i>Envío (Correo Argentino)
                </div>
              </div>
              <div class="col-md-3">
                <label class="field-label">Peso</label>
                <div class="field-group field-group--shipping">
                  <input
                    v-model.number="product.weight_input"
                    type="number"
                    class="field-input"
                    placeholder="1000"
                    min="0"
                    step="0.01"
                  />
                  <select v-model="product.weight_unit" class="field-input field-input--unit">
                    <option value="gr">gr</option>
                    <option value="kg">kg</option>
                    <option value="ml">ml</option>
                    <option value="cm3">cm³</option>
                  </select>
                </div>
                <span class="field-hint">
                  Al guardar se normaliza a gramos (Correo Argentino)
                  <template v-if="productWeightInGramsPreview != null">: ≈ {{ productWeightInGramsPreview }} g</template>
                </span>
              </div>
              <div class="col-md-3">
                <label class="field-label">Largo (cm)</label>
                <input v-model.number="product.length" type="number" class="field-input" placeholder="30" min="0" max="150" step="0.1" />
              </div>
              <div class="col-md-3">
                <label class="field-label">Ancho (cm)</label>
                <input v-model.number="product.width" type="number" class="field-input" placeholder="20" min="0" max="150" step="0.1" />
              </div>
              <div class="col-md-3">
                <label class="field-label">Alto (cm)</label>
                <input v-model.number="product.height" type="number" class="field-input" placeholder="15" min="0" max="150" step="0.1" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="form-section">
        <div class="section-indicator">
          <div class="indicator-icon indicator-icon--specs">
            <i class="fas fa-clipboard-list"></i>
          </div>
          <div class="indicator-line"></div>
        </div>
        <div class="section-content">
          <div class="section-label">Descripcion y Ficha Tecnica</div>
          <div class="row g-3">
            <div class="col-12">
              <div class="form-card details-equal-card">
                <label class="field-label">Descripcion</label>
                <div class="rich-editor">
                  <div class="editor-toolbar">
                    <button type="button" class="toolbar-btn" @click="formatText('bold')" title="Negrita"><i class="fas fa-bold"></i></button>
                    <button type="button" class="toolbar-btn" @click="formatText('italic')" title="Cursiva"><i class="fas fa-italic"></i></button>
                    <button type="button" class="toolbar-btn" @click="formatText('underline')" title="Subrayado"><i class="fas fa-underline"></i></button>
                    <span class="toolbar-divider"></span>
                    <button type="button" class="toolbar-btn" @click="insertList('ul')" title="Lista"><i class="fas fa-list-ul"></i></button>
                    <button type="button" class="toolbar-btn" @click="insertList('ol')" title="Lista numerada"><i class="fas fa-list-ol"></i></button>
                    <span class="toolbar-divider"></span>
                    <button type="button" class="toolbar-btn" @click="insertHeading('h3')" title="Subtitulo"><i class="fas fa-heading"></i></button>
                    <button type="button" class="toolbar-btn" @click="insertLink" title="Enlace"><i class="fas fa-link"></i></button>
                  </div>
                  <div ref="descriptionEditor" class="editor-content" contenteditable="true" @input="updateDescription" @paste="handleEditorPaste" placeholder="Escribe la descripcion del producto aqui..."></div>
                </div>
              </div>
            </div>
            <div class="col-12">
              <div class="form-card details-equal-card">
                <div class="subsection-label mb-2"><i class="fas fa-list-alt me-1"></i>Ficha tecnica</div>
                <div class="tech-sheet-builder">
                  <div class="tech-sheet-header">
                    <div class="tech-sheet-title">
                      <i :class="product.product_type ? currentProductTypeOption.icon : 'fas fa-layer-group'"></i>
                      <div>
                        <strong v-if="product.product_type">Ficha tecnica de {{ currentProductTypeOption.label.toLowerCase() }}</strong>
                        <strong v-else>Ficha tecnica sin tipo definido</strong>
                        <span v-if="product.product_type">{{ filledAttributeCount }} especificaciones cargadas</span>
                        <span v-else>Elegi el tipo para habilitar los inputs especificos.</span>
                      </div>
                    </div>
                    <div class="tech-sheet-actions">
                      <button type="button" class="btn-action btn-action-primary btn-action-sm btn-red" @click="openProductTypeModal"><i class="fas fa-tags"></i>{{ product.product_type ? ' Cambiar tipo de producto' : ' Elegir tipo de producto' }}</button>
                      <div class="add-spec-control" v-if="product.product_type && availableAttributeDefinitions.length">
                        <select v-model="attributeKeyToAdd" class="field-input field-input--sm">
                          <option value="">Elegir especificacion...</option>
                          <option v-for="def in availableAttributeDefinitions" :key="def.key" :value="def.key">{{ def.label }}</option>
                        </select>
                        <button type="button" class="btn-action btn-action-primary btn-action-sm btn-red" @click="addSelectedAttribute" :disabled="!attributeKeyToAdd"><i class="fas fa-plus"></i> Agregar</button>
                      </div>
                    </div>
                  </div>
                  <div v-if="!product.product_type" class="tech-sheet-empty"><i class="fas fa-info-circle"></i><span>Defini el tipo y despues agrega las especificaciones desde el selector.</span></div>
                  <div v-else-if="!selectedAttributeDefinitions.length" class="tech-sheet-empty"><i class="fas fa-list-alt"></i><span>No hay campos cargados para este tipo.</span></div>
                  <div v-for="group in groupedAttributeDefinitions" :key="group.name" class="attribute-group">
                    <div class="attribute-group-title"><i :class="group.icon"></i><span>{{ group.name }}</span></div>
                    <div class="attribute-grid">
                      <div v-for="def in group.items" :key="def.id ?? def.key" class="attribute-field" :class="{ 'attribute-field--highlight': def.is_highlight }">
                        <label class="field-label attribute-label"><i :class="attributeUi(def).icon"></i><span>{{ def.label }}</span></label>
                        <button type="button" class="attribute-remove" @click="removeAttribute(def.key)" title="Quitar especificacion"><i class="fas fa-times"></i></button>
                        <input v-if="def.data_type === 'string'" v-model="product.attributes[def.key]" type="text" class="field-input" :placeholder="attributePlaceholder(def)" />
                        <input v-else-if="def.data_type === 'number'" v-model.number="product.attributes[def.key]" type="number" class="field-input" step="any" :placeholder="attributePlaceholder(def)" />
                        <select v-else-if="def.data_type === 'select'" v-model="product.attributes[def.key]" class="field-input"><option value="">Sin especificar</option><option v-for="opt in selectOptionsForDef(def)" :key="def.key + '-' + opt.value" :value="opt.value">{{ opt.label }}</option></select>
                        <label v-else-if="def.data_type === 'boolean'" class="toggle-field attribute-toggle"><input type="checkbox" v-model="product.attributes[def.key]" class="toggle-input" /><span class="toggle-switch"></span><span class="toggle-label">{{ product.attributes[def.key] ? 'Si' : 'No' }}</span></label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Section: Precios -->
      <section class="form-section">
        <div class="section-indicator">
          <div class="indicator-icon indicator-icon--pricing">
            <i class="fas fa-dollar-sign"></i>
          </div>
          <div class="indicator-line"></div>
        </div>
        <div class="section-content">
          <div class="section-label">Precios y Descuentos</div>
          <div class="form-card">
            <div class="row g-3">
              <div class="col-md-4">
                <label class="field-label">Precio</label>
                <div class="price-input">
                  <span class="price-currency">{{ product.currency === 'USD' ? 'USD' : '$' }}</span>
                  <input v-model="product.price" type="number" class="field-input field-input--price" step="0.01" min="0" placeholder="0.00" />
                </div>
              </div>
              <div class="col-md-4">
                <label class="field-label">Moneda</label>
                <select v-model="product.currency" class="field-input">
                  <option value="ARS">ARS — Peso Argentino</option>
                  <option value="USD">USD — Dólar</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="field-label">Descuento</label>
                <div class="price-input">
                  <input v-model="product.discount" type="number" class="field-input field-input--price" step="1" min="0" max="100" placeholder="0" />
                  <span class="price-currency price-currency--right">%</span>
                </div>
              </div>

              <!-- Conversión informativa -->
              <div class="col-md-4" v-if="product.currency === 'ARS' && product.price > 0">
                <div class="conversion-badge" v-if="currentDollarRate">
                  <i class="fas fa-exchange-alt"></i>
                  <span>≈ USD {{ calculatePriceInUSD }}</span>
                </div>
                <div class="conversion-badge conversion-badge--warn" v-else>
                  <i class="fas fa-exclamation-triangle"></i>
                  <span>Tasa de dólar no disponible</span>
                </div>
              </div>

              <div class="col-12">
                <div class="divider"></div>
              </div>

              <div class="col-md-6">
                <label class="field-label">
                  <i class="fas fa-users me-1 text-muted"></i>Precio Mayorista
                </label>
                <div class="price-input">
                  <span class="price-currency">{{ product.currency === 'USD' ? 'USD' : '$' }}</span>
                  <input v-model="product.distributor_price" type="number" class="field-input field-input--price" step="0.01" min="0" placeholder="Opcional" />
                </div>
                <span class="field-hint">Precio especial para mayoristas</span>
              </div>
              <div class="col-md-3">
                <label class="field-label">Stock</label>
                <input v-model.number="product.stock" type="number" class="field-input" min="0" placeholder="0" />
              </div>
              <div class="col-md-3 d-flex align-items-end pb-1">
                <label class="toggle-field">
                  <input type="checkbox" v-model="product.is_restricted" class="toggle-input" />
                  <span class="toggle-switch"></span>
                  <span class="toggle-label"><i class="fas fa-lock me-1"></i>Restringido</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Section: Promoción -->
      <section class="form-section">
        <div class="section-indicator">
          <div class="indicator-icon indicator-icon--promo">
            <i class="fas fa-fire"></i>
          </div>
          <div class="indicator-line"></div>
        </div>
        <div class="section-content">
          <div class="section-label">Promoción Especial</div>
          <div class="form-card form-card--compact">
            <div class="d-flex align-items-center gap-3 flex-wrap">
              <label class="toggle-field">
                <input type="checkbox" v-model="product.has_special_promotion" class="toggle-input" />
                <span class="toggle-switch"></span>
                <span class="toggle-label">Activar promoción</span>
              </label>
              <transition name="slide-fade">
                <div v-if="product.has_special_promotion" class="flex-grow-1" style="min-width: 200px;">
                  <input 
                    v-model="product.special_promotion_name" 
                    type="text" 
                    class="field-input" 
                    placeholder="Ej: 3x2, 4x3, 2x1"
                    maxlength="50"
                  />
                </div>
              </transition>
            </div>
          </div>
        </div>
      </section>

      <!-- Section: Variantes -->
      <section class="form-section">
        <div class="section-indicator">
          <div class="indicator-icon indicator-icon--variants">
            <i class="fas fa-layer-group"></i>
          </div>
          <div class="indicator-line"></div>
        </div>
        <div class="section-content">
          <div class="section-label">
            Variantes
            <button type="button" class="add-variant-btn" @click="addVariantCategory">
              <i class="fas fa-plus"></i> Agregar tipo
            </button>
          </div>
          
          <!-- Empty state -->
          <div v-if="variants.length === 0" class="empty-state">
            <div class="empty-state-icon">
              <i class="fas fa-layer-group"></i>
            </div>
            <p>Sin variantes configuradas</p>
            <button type="button" class="btn-action btn-action-secondary btn-action-sm" @click="addVariantCategory">
              <i class="fas fa-plus me-1"></i>Agregar variante
            </button>
          </div>

          <!-- Variant categories -->
          <div v-for="(variant, variantIndex) in variants" :key="variantIndex" class="variant-block">
            <div class="variant-header">
              <div class="variant-header-left">
                <span class="variant-number">{{ variantIndex + 1 }}</span>
                <input
                  v-model="variant.variant_name"
                  type="text"
                  class="field-input variant-name-input"
                  placeholder="Nombre del tipo (ej: Tamaño, Color)"
                />
              </div>
              <div class="variant-header-actions">
                <button type="button" class="icon-btn icon-btn-brand" @click="addVariantOption(variantIndex)" title="Agregar opción">
                  <i class="fas fa-plus"></i>
                </button>
                <button type="button" class="icon-btn icon-btn-danger" @click="removeVariantCategory(variantIndex)" title="Eliminar tipo">
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>

            <!-- Options -->
            <div class="variant-options-list">
              <div v-if="!variant.options || variant.options.length === 0" class="variant-options-empty">
                <small>Agrega opciones para esta variante</small>
              </div>
              <TransitionGroup name="option-list" tag="div">
                <div 
                  v-for="(option, optionIndex) in variant.options" 
                  :key="optionIndex" 
                  class="option-row"
                >
                  <div class="option-fields">
                    <div class="option-field option-field--name">
                      <label class="option-label">Nombre</label>
                      <input v-model="option.name" type="text" class="field-input field-input--sm" placeholder="Ej: Grande, Rojo" />
                    </div>
                    <div class="option-field option-field--price">
                      <label class="option-label">Precio</label>
                      <div class="price-input price-input--sm">
                        <span class="price-currency">{{ product.currency === 'USD' ? 'USD' : '$' }}</span>
                        <input v-model.number="option.price" type="number" class="field-input field-input--sm field-input--price" min="0" step="0.01" placeholder="0" />
                      </div>
                      <small v-if="option.price && currentDollarRate" class="field-hint">
                        <template v-if="product.currency === 'ARS'">≈ USD {{ formatUSD(option.price / currentDollarRate) }}</template>
                        <template v-else>≈ {{ formatARS(option.price * currentDollarRate) }} ARS</template>
                      </small>
                    </div>
                    <div class="option-field option-field--stock">
                      <label class="option-label">Stock</label>
                      <input v-model.number="option.stock" type="number" class="field-input field-input--sm" placeholder="0" min="0" />
                    </div>
                    <div class="option-field option-field--sku">
                      <label class="option-label">SKU</label>
                      <div class="field-group field-group--sm">
                        <input v-model="option.sku" type="text" class="field-input field-input--sm" placeholder="Opcional" />
                        <button
                          type="button"
                          class="icon-btn icon-btn--sm"
                          @click="generateVariantSku(variantIndex, optionIndex)"
                          :disabled="generatingVariantSku[`${variantIndex}-${optionIndex}`]"
                          title="Generar SKU"
                        >
                          <i :class="generatingVariantSku[`${variantIndex}-${optionIndex}`] ? 'fas fa-spinner fa-spin' : 'fas fa-magic'"></i>
                        </button>
                      </div>
                    </div>
                    <div class="option-field option-field--discount">
                      <label class="option-label">Dto %</label>
                      <div class="price-input price-input--sm">
                        <input v-model.number="option.discount" type="number" class="field-input field-input--sm field-input--price" min="0" max="100" step="1" placeholder="0" />
                        <span class="price-currency price-currency--right">%</span>
                      </div>
                    </div>
                    <div class="option-field option-field--distrib">
                      <label class="option-label">Distrib.</label>
                      <div class="price-input price-input--sm">
                        <span class="price-currency">{{ product.currency === 'USD' ? 'USD' : '$' }}</span>
                        <input v-model.number="option.distributor_price" type="number" class="field-input field-input--sm field-input--price" min="0" step="0.01" placeholder="—" />
                      </div>
                    </div>
                    <div class="option-field option-field--shipping">
                      <label class="option-label"><i class="fas fa-truck me-1 opacity-60"></i>Envío</label>
                      <div class="option-shipping-row">
                        <input v-model.number="option.weight_input" type="number" class="field-input field-input--sm" placeholder="Peso" min="0" step="0.01" style="width: 70px;" />
                        <select v-model="option.weight_unit" class="field-input field-input--sm field-input--unit" style="width: 60px;">
                          <option value="gr">gr</option>
                          <option value="kg">kg</option>
                          <option value="ml">ml</option>
                          <option value="cm3">cm³</option>
                        </select>
                        <input v-model.number="option.length" type="number" class="field-input field-input--sm" placeholder="L" min="0" max="150" style="width: 45px;" title="Largo cm" />
                        <input v-model.number="option.width" type="number" class="field-input field-input--sm" placeholder="A" min="0" max="150" style="width: 45px;" title="Ancho cm" />
                        <input v-model.number="option.height" type="number" class="field-input field-input--sm" placeholder="Al" min="0" max="150" style="width: 45px;" title="Alto cm" />
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="option-remove"
                    @click="removeVariantOption(variantIndex, optionIndex)"
                    title="Eliminar opción"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </TransitionGroup>
            </div>
          </div>
        </div>
      </section>

      <!-- Section: Imágenes -->
      <section class="form-section">
        <div class="section-indicator">
          <div class="indicator-icon indicator-icon--images">
            <i class="fas fa-image"></i>
          </div>
        </div>
        <div class="section-content">
          <div class="section-label">Imágenes</div>
          <div class="form-card" @paste="handlePaste">
            <div class="images-grid">
              <!-- Image 1 -->
              <div class="image-slot">
                <div class="image-slot-label">Principal</div>
                <div v-if="imagePreview" class="image-preview-card">
                  <img :src="bustedPreviewSrc(imagePreview)" alt="Imagen principal" />
                  <button type="button" class="image-remove-btn" @click="removeImage('image')">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <label v-else class="image-upload-zone">
                  <input type="file" class="d-none" @change="handleUploadImage($event, 'image')" ref="imageUrlInput" accept="image/*" />
                  <i class="fas fa-cloud-upload-alt"></i>
                  <span>Arrastra o haz clic</span>
                  <small>1000×1000px recomendado</small>
                </label>
                <input v-if="imagePreview" type="file" class="field-input field-input--sm mt-2" @change="handleUploadImage($event, 'image')" ref="imageUrlInput" accept="image/*" />
              </div>
              <!-- Image 2 -->
              <div class="image-slot">
                <div class="image-slot-label">Secundaria</div>
                <div v-if="imagePreview2" class="image-preview-card">
                  <img :src="bustedPreviewSrc(imagePreview2)" alt="Imagen secundaria" />
                  <button type="button" class="image-remove-btn" @click="removeImage('image_2')">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <label v-else class="image-upload-zone">
                  <input type="file" class="d-none" @change="handleUploadImage($event, 'image_2')" ref="imageUrlInput2" accept="image/*" />
                  <i class="fas fa-cloud-upload-alt"></i>
                  <span>Arrastra o haz clic</span>
                  <small>1000×1000px recomendado</small>
                </label>
                <input v-if="imagePreview2" type="file" class="field-input field-input--sm mt-2" @change="handleUploadImage($event, 'image_2')" ref="imageUrlInput2" accept="image/*" />
              </div>
              <!-- Image 3 -->
              <div class="image-slot">
                <div class="image-slot-label">Imagen 3</div>
                <div v-if="imagePreview3" class="image-preview-card">
                  <img :src="bustedPreviewSrc(imagePreview3)" alt="Imagen 3" />
                  <button type="button" class="image-remove-btn" @click="removeImage('image_3')">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <label v-else class="image-upload-zone">
                  <input type="file" class="d-none" @change="handleUploadImage($event, 'image_3')" ref="imageUrlInput3" accept="image/*" />
                  <i class="fas fa-cloud-upload-alt"></i>
                  <span>Arrastra o haz clic</span>
                  <small>1000×1000px recomendado</small>
                </label>
                <input v-if="imagePreview3" type="file" class="field-input field-input--sm mt-2" @change="handleUploadImage($event, 'image_3')" ref="imageUrlInput3" accept="image/*" />
              </div>
              <!-- Image 4 -->
              <div class="image-slot">
                <div class="image-slot-label">Imagen 4</div>
                <div v-if="imagePreview4" class="image-preview-card">
                  <img :src="bustedPreviewSrc(imagePreview4)" alt="Imagen 4" />
                  <button type="button" class="image-remove-btn" @click="removeImage('image_4')">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                <label v-else class="image-upload-zone">
                  <input type="file" class="d-none" @change="handleUploadImage($event, 'image_4')" ref="imageUrlInput4" accept="image/*" />
                  <i class="fas fa-cloud-upload-alt"></i>
                  <span>Arrastra o haz clic</span>
                  <small>1000×1000px recomendado</small>
                </label>
                <input v-if="imagePreview4" type="file" class="field-input field-input--sm mt-2" @change="handleUploadImage($event, 'image_4')" ref="imageUrlInput4" accept="image/*" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Floating save bar (mobile) -->
      <div class="floating-save">
        <button type="submit" class="btn-action btn-action-primary btn-action-full" :disabled="isSubmitting">
          <i :class="isSubmitting ? 'fas fa-spinner fa-spin' : 'fas fa-save'" class="me-2"></i>
          {{ isSubmitting ? 'Guardando...' : (isEditMode ? 'Actualizar Producto' : 'Guardar Producto') }}
        </button>
      </div>
    </form>

    <!-- Modal de confirmación para eliminar categoría -->
    <div class="modal fade" id="deleteCategoryModal" tabindex="-1" aria-labelledby="deleteCategoryModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content modal-dark">
          <div class="modal-header">
            <h5 class="modal-title" id="deleteCategoryModalLabel">
              <i class="fas fa-exclamation-triangle text-warning me-2"></i>Eliminar Categoría
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body" v-if="selectedCategoryForDeletion">
            <p>¿Eliminar la categoría "<strong class="text-white">{{ getCategoryName(selectedCategoryForDeletion) }}</strong>"?</p>
            <p class="text-warning small mb-0">
              <i class="fas fa-info-circle me-1"></i>
              Solo se pueden eliminar categorías sin productos asociados.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-action btn-action-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button 
              type="button" 
              class="btn-action btn-action-danger" 
              @click="handleDeleteCategory" 
              :disabled="deletingCategory"
            >
              <i :class="deletingCategory ? 'fas fa-spinner fa-spin' : 'fas fa-trash-alt'" class="me-1"></i>
              {{ deletingCategory ? 'Eliminando...' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade" id="chooseProductTypeModal" tabindex="-1" aria-labelledby="chooseProductTypeModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content modal-dark">
          <div class="modal-header">
            <h5 class="modal-title" id="chooseProductTypeModalLabel">
              <i class="fas fa-tags text-danger me-2"></i>Elegir tipo de producto
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="product-type-modal-grid">
              <button
                v-for="type in visibleProductTypeOptions"
                :key="type.value"
                type="button"
                class="product-type-card product-type-card--modal"
                :class="{ 'product-type-card--active': product.product_type === type.value }"
                @click="chooseProductTypeFromModal(type.value)"
              >
                <i :class="type.icon"></i>
                <span>{{ type.label }}</span>
                <small>{{ type.description }}</small>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getProduct, getCategories, createCategory, deleteCategory, generateProductSku, getProductAttributeDefinitions } from "@/services/products";
import { createProduct, updateProduct } from "@/services/products";
// toastification
import { useToast } from "vue-toastification";
import "vue-toastification/dist/index.css";
import * as bootstrap from 'bootstrap'; // Correct import for Bootstrap
import { getCurrentDollarRate } from '@/services/payment';
import {
  PRODUCT_TYPE_OPTIONS,
  getAttributeUi,
  getProductTypeOption,
  normalizeSelectOptions,
} from '@/helpers/productAttributes';

// Configuración de toastification
const toast = useToast();

// Estructura inicial del producto actualizada
const initialProduct = {
  name: "",
  brand: "",
  sku: "",
  category: null,
  category_name: "",
  product_type: null,
  attributes: {},
  description: "",
  volume: "",
  dimensions: "",
  weight: null,
  weight_input: null,
  weight_unit: "gr",
  length: null,
  width: null,
  height: null,
  price: 0,
  discount: 0,
  distributor_price: null,
  is_restricted: false,
  currency: "ARS",  // Default currency to ARS
  stock: 0,
  image: null,
  image_2: null,
  image_3: null,
  image_4: null,
  is_available: true,
  has_variants: false,
  has_special_promotion: false,
  special_promotion_name: ''
};

const product = ref({ ...initialProduct });
const imagePreview = ref(null);
const imagePreview2 = ref(null);
const imagePreview3 = ref(null);
const imagePreview4 = ref(null);
/** Evita que el navegador muestre una imagen en caché cuando la URL no cambia */
const imageCacheBust = ref(0);
/** Archivos locales pendientes; la subida al CDN la hace el backend al guardar el producto */
const pendingImageFile = ref(null);
const pendingImage2File = ref(null);
const pendingImage3File = ref(null);
const pendingImage4File = ref(null);

const imageUrlInput = ref(null);
const imageUrlInput2 = ref(null);
const imageUrlInput3 = ref(null);
const imageUrlInput4 = ref(null);

const revokeIfBlobUrl = (url) => {
  if (url && String(url).startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

const bustedPreviewSrc = (url) => {
  if (!url) return '';
  const s = String(url);
  if (s.startsWith('blob:')) return s;
  const sep = s.includes('?') ? '&' : '?';
  return `${s}${sep}_pv=${imageCacheBust.value}`;
};

/** Sincroniza URLs de imagen desde la respuesta API (evita mostrar datos viejos antes del GET forzado). */
const applyImageFieldsFromApi = (data) => {
  if (!data || typeof data !== 'object') return;
  let bump = false;
  if (data.image !== undefined) {
    product.value.image = data.image;
    revokeIfBlobUrl(imagePreview.value);
    imagePreview.value = data.image || null;
    bump = true;
  }
  if (data.image_2 !== undefined) {
    product.value.image_2 = data.image_2;
    revokeIfBlobUrl(imagePreview2.value);
    imagePreview2.value = data.image_2 || null;
    bump = true;
  }
  if (data.image_3 !== undefined) {
    product.value.image_3 = data.image_3;
    revokeIfBlobUrl(imagePreview3.value);
    imagePreview3.value = data.image_3 || null;
    bump = true;
  }
  if (data.image_4 !== undefined) {
    product.value.image_4 = data.image_4;
    revokeIfBlobUrl(imagePreview4.value);
    imagePreview4.value = data.image_4 || null;
    bump = true;
  }
  if (bump) imageCacheBust.value += 1;
};

const isSubmitting = ref(false);
const isEditMode = ref(false);
const route = useRoute();
const router = useRouter();
const productId = route.params.productId;
const categories = ref([]);

// Variables para el precio del dólar
const currentDollarRate = ref(null);
const dollarUpdateDate = ref(null);

// SKU generation refs
const generatingBaseSku = ref(false);
const generatingVariantSku = ref({});

// New refs for category creation
const showCategoryInput = ref(false);
const newCategoryName = ref('');
const creatingCategory = ref(false);

// New refs for category deletion
let modalInstance = null; // Store the actual Modal instance
let productTypeModalInstance = null;
const selectedCategoryForDeletion = ref(null);
const deletingCategory = ref(false);

// Function to toggle between category selection and new category input
const toggleCategoryInput = () => {
  showCategoryInput.value = !showCategoryInput.value;
};

// Function to create a new category
const createNewCategory = async () => {
  if (!newCategoryName.value.trim()) {
    toast.error("El nombre de la categoría es requerido");
    return;
  }

  try {
    creatingCategory.value = true;
    const categoryData = { name: newCategoryName.value.trim() };
    const response = await createCategory(categoryData);
    
    // Add the new category to the categories list
    categories.value.push(response);
    
    // Select the newly created category
    product.value.category = response.id;
    
    // Reset the form
    newCategoryName.value = '';
    showCategoryInput.value = false;
    
    toast.success("Categoría creada exitosamente");
  } catch (error) {
    console.error('Error creating category:', error);
    toast.error(error.message || 'Error al crear la categoría');
  } finally {
    creatingCategory.value = false;
  }
};

// Function to get the category name by id
const getCategoryName = (categoryId) => {
  const category = categories.value.find(c => c.id === categoryId);
  return category ? category.name : '';
};

// Open modal to confirm category deletion
const confirmDeleteCategory = () => {
  if (!product.value.category) return;
  
  selectedCategoryForDeletion.value = product.value.category;
  
  // Use Bootstrap modal
  if (!modalInstance) {
    const modalElement = document.getElementById('deleteCategoryModal');
    modalInstance = new bootstrap.Modal(modalElement);
  }
  
  modalInstance.show();
};

// Handle category deletion
const handleDeleteCategory = async () => {
  if (!selectedCategoryForDeletion.value) return;
  
  try {
    deletingCategory.value = true;
    await deleteCategory(selectedCategoryForDeletion.value);
    
    // Remove from categories list
    const index = categories.value.findIndex(c => c.id === selectedCategoryForDeletion.value);
    if (index !== -1) {
      categories.value.splice(index, 1);
    }
    
    // Reset product category if it was the deleted one
    if (product.value.category === selectedCategoryForDeletion.value) {
      product.value.category = null;
    }
    
    // Close modal
    modalInstance.hide();
    selectedCategoryForDeletion.value = null;
    
    toast.success("Categoría eliminada exitosamente");
  } catch (error) {
    console.error('Error deleting category:', error);
    toast.error(error.message || "Error al eliminar la categoría. Es posible que esté siendo utilizada por productos.");
  } finally {
    deletingCategory.value = false;
  }
};

const variants = ref([]);

/** Definiciones del tipo de producto (formulario dinámico) */
const attributeDefinitions = ref([]);
const selectedAttributeKeys = ref([]);
const attributeKeyToAdd = ref('');
const productTypeOptions = PRODUCT_TYPE_OPTIONS;
const VISIBLE_PRODUCT_TYPES = [
  'phone',
  'tablet',
  'smartwatch',
  'notebook',
  'headphones',
  'speaker',
  'charger',
  'cable',
  'case',
  'screen_protector',
  'power_bank',
  'storage',
];
const visibleProductTypeOptions = computed(() =>
  productTypeOptions.filter((item) => VISIBLE_PRODUCT_TYPES.includes(item.value)),
);
const DEFAULT_ATTRIBUTE_KEYS_BY_TYPE = {
  phone: [
    'storage',
    'ram',
    'screen_size',
    'processor',
    'main_camera',
    'front_camera',
    'battery',
    'charging_power',
    'os',
    'network',
    'sim',
    'nfc',
    'bluetooth',
    'wifi',
    'weight_device',
    'water_resistance',
    'dimensions',
  ],
  tablet: ['storage', 'ram', 'dimensions', 'battery', 'screen_size', 'processor'],
  smartwatch: ['battery_life', 'screen_size', 'water_resistance', 'compatibility'],
  notebook: ['storage', 'ram', 'screen_size', 'processor', 'gpu', 'dimensions'],
  headphones: ['connection_type', 'battery_life', 'noise_cancellation', 'water_resistance'],
  speaker: ['power_output', 'connection_type', 'battery_life', 'water_resistance'],
  charger: ['power_output', 'connector', 'ports', 'fast_charge'],
  cable: ['connector', 'length_cable', 'max_power', 'data_transfer'],
  case: ['compatible_model', 'material', 'case_type', 'color'],
  screen_protector: ['compatible_model', 'protector_type', 'hardness', 'coverage'],
  power_bank: ['capacity_mah', 'power_output', 'ports', 'fast_charge'],
  storage: ['storage_type', 'storage', 'speed_class', 'connector'],
};
const LOCAL_FALLBACK_DEFINITIONS = {
  phone: [
    { key: 'storage', label: 'Capacidad de almacenamiento', data_type: 'select', options: ['64 GB', '128 GB', '256 GB', '512 GB', '1 TB'] },
    { key: 'ram', label: 'Memoria RAM', data_type: 'select', options: ['4 GB', '6 GB', '8 GB', '12 GB', '16 GB'] },
    { key: 'screen_size', label: 'Tamano de pantalla', data_type: 'string' },
    { key: 'os', label: 'Sistema operativo', data_type: 'select', options: ['Android', 'iOS'] },
    { key: 'network', label: 'Red movil', data_type: 'select', options: ['4G LTE', '5G'] },
    { key: 'sim', label: 'Tipo de SIM', data_type: 'select', options: ['Single SIM', 'Dual SIM', 'eSIM'] },
    { key: 'wifi', label: 'WiFi', data_type: 'string' },
    { key: 'bluetooth', label: 'Bluetooth', data_type: 'string' },
    { key: 'nfc', label: 'NFC', data_type: 'boolean' },
    { key: 'battery', label: 'Bateria', data_type: 'string' },
    { key: 'charging_power', label: 'Carga rapida', data_type: 'string' },
    { key: 'main_camera', label: 'Camara principal', data_type: 'string' },
    { key: 'front_camera', label: 'Camara frontal', data_type: 'string' },
    { key: 'processor', label: 'Procesador', data_type: 'string' },
    { key: 'weight_device', label: 'Peso del equipo', data_type: 'string' },
    { key: 'water_resistance', label: 'Resistencia al agua', data_type: 'string' },
    { key: 'dimensions', label: 'Dimensiones', data_type: 'string' },
  ],
  speaker: [
    { key: 'power_output', label: 'Potencia', data_type: 'string' },
    { key: 'connection_type', label: 'Tipo de conexion', data_type: 'select', options: ['Bluetooth', 'AUX', 'USB', 'WiFi'] },
    { key: 'battery_life', label: 'Autonomia', data_type: 'string' },
    { key: 'water_resistance', label: 'Resistencia al agua', data_type: 'string' },
    { key: 'dimensions', label: 'Dimensiones', data_type: 'string' },
  ],
  tablet: [
    { key: 'storage', label: 'Capacidad de almacenamiento', data_type: 'select', options: ['64 GB', '128 GB', '256 GB', '512 GB', '1 TB'] },
    { key: 'ram', label: 'Memoria RAM', data_type: 'select', options: ['4 GB', '6 GB', '8 GB', '12 GB'] },
    { key: 'screen_size', label: 'Tamano de pantalla', data_type: 'string' },
    { key: 'battery', label: 'Bateria', data_type: 'string' },
    { key: 'processor', label: 'Procesador', data_type: 'string' },
    { key: 'dimensions', label: 'Dimensiones', data_type: 'string' },
  ],
  notebook: [
    { key: 'storage', label: 'Capacidad de almacenamiento', data_type: 'select', options: ['256 GB SSD', '512 GB SSD', '1 TB SSD', '2 TB SSD'] },
    { key: 'ram', label: 'Memoria RAM', data_type: 'select', options: ['8 GB', '16 GB', '32 GB', '64 GB'] },
    { key: 'screen_size', label: 'Tamano de pantalla', data_type: 'string' },
    { key: 'processor', label: 'Procesador', data_type: 'string' },
    { key: 'gpu', label: 'Placa de video', data_type: 'string' },
    { key: 'dimensions', label: 'Dimensiones', data_type: 'string' },
  ],
  smartwatch: [
    { key: 'screen_size', label: 'Tamano de pantalla', data_type: 'string' },
    { key: 'battery_life', label: 'Autonomia', data_type: 'string' },
    { key: 'water_resistance', label: 'Resistencia al agua', data_type: 'string' },
    { key: 'compatibility', label: 'Compatibilidad', data_type: 'string' },
    { key: 'dimensions', label: 'Dimensiones', data_type: 'string' },
  ],
  headphones: [
    { key: 'connection_type', label: 'Tipo de conexion', data_type: 'select', options: ['Bluetooth', '3.5 mm', 'USB-C', 'Lightning'] },
    { key: 'battery_life', label: 'Autonomia', data_type: 'string' },
    { key: 'noise_cancellation', label: 'Cancelacion de ruido', data_type: 'boolean' },
    { key: 'water_resistance', label: 'Resistencia al agua', data_type: 'string' },
    { key: 'dimensions', label: 'Dimensiones', data_type: 'string' },
  ],
  charger: [
    { key: 'power_output', label: 'Potencia', data_type: 'string' },
    { key: 'connector', label: 'Conector', data_type: 'select', options: ['USB-A', 'USB-C', 'Lightning', 'Inalambrico'] },
    { key: 'ports', label: 'Puertos', data_type: 'string' },
    { key: 'fast_charge', label: 'Carga rapida', data_type: 'boolean' },
    { key: 'dimensions', label: 'Dimensiones', data_type: 'string' },
  ],
  cable: [
    { key: 'connector', label: 'Conector', data_type: 'string' },
    { key: 'length_cable', label: 'Largo del cable', data_type: 'string' },
    { key: 'max_power', label: 'Potencia maxima', data_type: 'string' },
    { key: 'data_transfer', label: 'Transferencia de datos', data_type: 'boolean' },
    { key: 'material', label: 'Material', data_type: 'string' },
  ],
  case: [
    { key: 'compatible_model', label: 'Modelo compatible', data_type: 'string' },
    { key: 'material', label: 'Material', data_type: 'string' },
    { key: 'case_type', label: 'Tipo de funda', data_type: 'string' },
    { key: 'color', label: 'Color', data_type: 'string' },
    { key: 'dimensions', label: 'Dimensiones', data_type: 'string' },
  ],
  screen_protector: [
    { key: 'compatible_model', label: 'Modelo compatible', data_type: 'string' },
    { key: 'protector_type', label: 'Tipo de protector', data_type: 'string' },
    { key: 'hardness', label: 'Dureza', data_type: 'string' },
    { key: 'coverage', label: 'Cobertura', data_type: 'string' },
    { key: 'dimensions', label: 'Dimensiones', data_type: 'string' },
  ],
  power_bank: [
    { key: 'capacity_mah', label: 'Capacidad (mAh)', data_type: 'string' },
    { key: 'power_output', label: 'Potencia de salida', data_type: 'string' },
    { key: 'ports', label: 'Puertos', data_type: 'string' },
    { key: 'fast_charge', label: 'Carga rapida', data_type: 'boolean' },
    { key: 'dimensions', label: 'Dimensiones', data_type: 'string' },
  ],
  storage: [
    { key: 'storage_type', label: 'Tipo de almacenamiento', data_type: 'string' },
    { key: 'storage', label: 'Capacidad de almacenamiento', data_type: 'string' },
    { key: 'speed_class', label: 'Clase de velocidad', data_type: 'string' },
    { key: 'connector', label: 'Conector', data_type: 'string' },
    { key: 'dimensions', label: 'Dimensiones', data_type: 'string' },
  ],
};

const normalizeFallbackDefinitions = (defs = []) =>
  defs.map((def, index) => ({
    id: `local-${def.key}`,
    product_type: '',
    key: def.key,
    label: def.label,
    data_type: def.data_type || 'string',
    required: false,
    options: def.options || null,
    order: index,
    is_highlight: index < 4,
  }));

const currentProductTypeOption = computed(() => getProductTypeOption(product.value.product_type));

const attributeUi = (def) => getAttributeUi(def?.key);

const groupIconForName = (name) => ({
  Pantalla: 'fas fa-expand',
  Rendimiento: 'fas fa-microchip',
  Camara: 'fas fa-camera',
  Energia: 'fas fa-bolt',
  Conectividad: 'fas fa-wifi',
  Compatibilidad: 'fas fa-check-circle',
  Fisico: 'fas fa-ruler-combined',
  Proteccion: 'fas fa-shield-alt',
  Audio: 'fas fa-headphones',
  Servicio: 'fas fa-tools',
  General: 'fas fa-info-circle',
}[name] || 'fas fa-list-alt');

const selectedAttributeDefinitions = computed(() =>
  attributeDefinitions.value.filter((def) => selectedAttributeKeys.value.includes(def.key)),
);

const availableAttributeDefinitions = computed(() =>
  attributeDefinitions.value.filter((def) => !selectedAttributeKeys.value.includes(def.key)),
);

const groupedAttributeDefinitions = computed(() => {
  const groups = new Map();
  for (const def of selectedAttributeDefinitions.value) {
    const ui = attributeUi(def);
    const groupName = ui.group || 'General';
    if (!groups.has(groupName)) {
      groups.set(groupName, {
        name: groupName,
        icon: groupIconForName(groupName),
        items: [],
      });
    }
    groups.get(groupName).items.push(def);
  }
  return [...groups.values()];
});

const filledAttributeCount = computed(() =>
  selectedAttributeDefinitions.value.filter((def) => {
    const value = product.value.attributes?.[def.key];
    return value !== null && value !== undefined && value !== '';
  }).length,
);

const attributePlaceholder = (def) => {
  const examples = {
    screen_size: 'Ej: 6.7" OLED 120 Hz',
    processor: 'Ej: Snapdragon 8 Gen 2',
    ram: 'Ej: 8 GB',
    storage: 'Ej: 256 GB',
    main_camera: 'Ej: 48 MP + 12 MP',
    battery: 'Ej: 5000 mAh',
    battery_life: 'Ej: hasta 30 horas',
    power_output: 'Ej: 20 W',
    compatible_model: 'Ej: iPhone 15 Pro Max',
    dimensions: 'Ej: 160 x 77 x 8 mm',
    weight_device: 'Ej: 190 g',
    warranty: 'Ej: 30 dias',
  };
  return examples[def?.key] || def?.label || '';
};

const loadAttributeDefinitions = async (pt) => {
  try {
    if (!pt) {
      attributeDefinitions.value = [];
      selectedAttributeKeys.value = [];
      attributeKeyToAdd.value = '';
      return;
    }
    const list = await getProductAttributeDefinitions(pt);
    const serverDefs = Array.isArray(list) ? list : [];
    const localDefs = normalizeFallbackDefinitions(LOCAL_FALLBACK_DEFINITIONS[pt] || []);
    attributeDefinitions.value = serverDefs.length ? serverDefs : localDefs;
    if (!product.value.attributes || typeof product.value.attributes !== 'object') {
      product.value.attributes = {};
    }
    selectedAttributeKeys.value = Object.keys(product.value.attributes).filter((key) =>
      attributeDefinitions.value.some((def) => def.key === key)
      && product.value.attributes[key] !== null
      && product.value.attributes[key] !== undefined
      && product.value.attributes[key] !== '',
    );
  } catch (e) {
    console.error('Error loading attribute definitions:', e);
    attributeDefinitions.value = normalizeFallbackDefinitions(LOCAL_FALLBACK_DEFINITIONS[pt] || []);
    selectedAttributeKeys.value = [];
  }
};

const selectProductType = async (type) => {
  if (product.value.product_type === type) return;
  product.value.product_type = type;
  product.value.attributes = {};
  selectedAttributeKeys.value = [];
  attributeKeyToAdd.value = '';
  await loadAttributeDefinitions(type);
  applyDefaultAttributesForType(type);
};

const openProductTypeModal = () => {
  if (!productTypeModalInstance) {
    const modalElement = document.getElementById('chooseProductTypeModal');
    if (!modalElement) return;
    productTypeModalInstance = new bootstrap.Modal(modalElement);
  }
  productTypeModalInstance.show();
};

const chooseProductTypeFromModal = async (type) => {
  await selectProductType(type);
  if (productTypeModalInstance) {
    productTypeModalInstance.hide();
  }
};


const defaultAttributeValue = (def) => {
  if (def?.data_type === 'boolean') return false;
  if (def?.data_type === 'number') return null;
  return '';
};

const applyDefaultAttributesForType = (type) => {
  const preferredKeys = DEFAULT_ATTRIBUTE_KEYS_BY_TYPE[type] || [];
  const validPreferred = preferredKeys.filter((key) =>
    attributeDefinitions.value.some((def) => def.key === key),
  );
  const fallback = attributeDefinitions.value.slice(0, 6).map((def) => def.key);
  const limitByType = type === 'phone' ? 16 : 8;
  const finalKeys = (validPreferred.length ? validPreferred : fallback).slice(0, limitByType);

  selectedAttributeKeys.value = finalKeys;
  for (const key of finalKeys) {
    if (!(key in product.value.attributes)) {
      const def = attributeDefinitions.value.find((item) => item.key === key);
      product.value.attributes[key] = defaultAttributeValue(def);
    }
  }
};

const addSelectedAttribute = () => {
  const key = attributeKeyToAdd.value;
  if (!key || selectedAttributeKeys.value.includes(key)) return;
  const def = attributeDefinitions.value.find((item) => item.key === key);
  if (!def) return;
  selectedAttributeKeys.value = [...selectedAttributeKeys.value, key];
  if (!product.value.attributes || typeof product.value.attributes !== 'object') {
    product.value.attributes = {};
  }
  if (!(key in product.value.attributes)) {
    product.value.attributes[key] = defaultAttributeValue(def);
  }
  attributeKeyToAdd.value = '';
};

const removeAttribute = (key) => {
  selectedAttributeKeys.value = selectedAttributeKeys.value.filter((item) => item !== key);
  if (product.value.attributes && key in product.value.attributes) {
    const next = { ...product.value.attributes };
    delete next[key];
    product.value.attributes = next;
  }
};

const selectOptionsForDef = (def) => normalizeSelectOptions(def?.options);

// Rich text editor ref
const descriptionEditor = ref(null);

// Rich text editor functions
const formatText = (command, value = null) => {
  document.execCommand(command, false, value);
};

const insertLink = () => {
  const url = prompt('Ingresa la URL del enlace:');
  if (url) {
    formatText('createLink', url);
  }
};

const insertList = (type) => {
  formatText(type === 'ul' ? 'insertUnorderedList' : 'insertOrderedList');
};

const insertHeading = (tag = 'h3') => {
  const selection = window.getSelection();
  if (selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    const selectedText = range.toString() || 'Subtítulo';
    const element = document.createElement(tag);
    element.textContent = selectedText;
    element.className = 'text-white fw-bold mb-2';
    range.deleteContents();
    range.insertNode(element);
  }
};

const updateDescription = (event) => {
  product.value.description = event.target.innerHTML;
};

const handleEditorPaste = (event) => {
  event.preventDefault();
  const text = event.clipboardData.getData('text/plain');
  document.execCommand('insertText', false, text);
};

const initDescriptionEditor = () => {
  if (descriptionEditor.value && product.value.description) {
    descriptionEditor.value.innerHTML = product.value.description;
  }
};

// New variant handling functions
const addVariantCategory = () => {
  variants.value.push({
    variant_name: '',
    options: []
  });
  console.log('Added new variant category. Current variants:', variants.value);
};

const removeVariantCategory = (variantIndex) => {
  variants.value.splice(variantIndex, 1);
};

const addVariantOption = (variantIndex) => {
  if (!variants.value[variantIndex].options) {
    variants.value[variantIndex].options = [];
  }
  
  variants.value[variantIndex].options.push({
    name: '',
    price: 0,
    stock: 0,
    discount: 0,
    sku: '',
    distributor_price: null,
    weight: null,
    weight_input: null,
    weight_unit: 'gr',
    length: null,
    width: null,
    height: null
  });
};

const formatARS = (value) => {
  if (!value || isNaN(value)) return '0';
  return Math.round(value).toLocaleString('es-AR');
};

const formatUSD = (value) => {
  if (!value || isNaN(value)) return '0.00';
  return parseFloat(value).toFixed(2);
};

/** Convierte peso a gramos según unidad: gr, kg, ml, cm3 */
const toGrams = (value, unit) => {
  if (value == null || value === '' || isNaN(Number(value))) return null;
  const n = Number(value);
  if (n <= 0) return null;
  switch (unit) {
    case 'kg': return Math.round(n * 1000 * 1000) / 1000;
    case 'ml':
    case 'cm3': return Math.round(n * 1000) / 1000; // ~1 g/ml, 1 g/cm³
    default: return Math.round(n * 1000) / 1000; // gr
  }
};



const generateBaseSku = async () => {
  if (!product.value.name) {
    toast.error('Ingresa el nombre del producto primero');
    return;
  }
  generatingBaseSku.value = true;
  try {
    const categoryName = getCategoryName(product.value.category);
    const sku = await generateProductSku(product.value.name, categoryName);
    product.value.sku = sku;
  } catch (error) {
    toast.error(error.message || 'Error al generar SKU');
  } finally {
    generatingBaseSku.value = false;
  }
};

const generateVariantSku = async (variantIndex, optionIndex) => {
  const option = variants.value[variantIndex]?.options?.[optionIndex];
  if (!option?.name) {
    toast.error('Ingresa el nombre de la opción primero');
    return;
  }
  const key = `${variantIndex}-${optionIndex}`;
  generatingVariantSku.value = { ...generatingVariantSku.value, [key]: true };
  try {
    const categoryName = getCategoryName(product.value.category);
    const sku = await generateProductSku(product.value.name, categoryName, option.name);
    option.sku = sku;
  } catch (error) {
    toast.error(error.message || 'Error al generar SKU de variante');
  } finally {
    generatingVariantSku.value = { ...generatingVariantSku.value, [key]: false };
  }
};

const removeVariantOption = (variantIndex, optionIndex) => {
  variants.value[variantIndex].options.splice(optionIndex, 1);
};


// Función para cargar los datos del producto
const loadProductData = async (id, force = false) => {
  if (!id) return;
  
  isEditMode.value = true;
  try {
    isSubmitting.value = true;
  const productData = await getProduct(id, force);
    
    if (!productData) {
      throw new Error('No se encontró el producto');
    }

    console.log('API product data:', productData); // Debug log

    revokeIfBlobUrl(imagePreview.value);
    revokeIfBlobUrl(imagePreview2.value);
    revokeIfBlobUrl(imagePreview3.value);
    revokeIfBlobUrl(imagePreview4.value);
    pendingImageFile.value = null;
    pendingImage2File.value = null;
    pendingImage3File.value = null;
    pendingImage4File.value = null;

    if (productData.image) {
      imagePreview.value = productData.image;
    } else {
      imagePreview.value = null;
    }
    if (productData.image_2) {
      imagePreview2.value = productData.image_2;
    } else {
      imagePreview2.value = null;
    }
    if (productData.image_3) {
      imagePreview3.value = productData.image_3;
    } else {
      imagePreview3.value = null;
    }
    if (productData.image_4) {
      imagePreview4.value = productData.image_4;
    } else {
      imagePreview4.value = null;
    }
    if (force && (productData.image || productData.image_2 || productData.image_3 || productData.image_4)) {
      imageCacheBust.value += 1;
    }
    
    // Update product data
    product.value = {
      ...initialProduct,
      ...productData,
      category: productData.category?.id || productData.category || null,
      brand: productData.brand || '',
      sku: productData.sku || '',
      product_type: productData.product_type || null,
      attributes:
        productData.attributes &&
        typeof productData.attributes === 'object' &&
        !Array.isArray(productData.attributes)
          ? { ...productData.attributes }
          : {},
      description: productData.description || '',
      volume: productData.volume || '',
      dimensions: productData.dimensions || '',
      weight: productData.weight ?? null,
      weight_input: productData.weight != null ? Number(productData.weight) : null,
      weight_unit: 'gr',
      length: productData.length != null ? Number(productData.length) : null,
      width: productData.width != null ? Number(productData.width) : null,
      height: productData.height != null ? Number(productData.height) : null,
      has_variants: productData.has_variants || false,
      price: productData.price || 0,
      discount: productData.discount || 0,
      distributor_price: productData.distributor_price || null,
      is_restricted: productData.is_restricted || false,
      stock: productData.stock || 0,
      is_available: productData.is_available !== undefined ? 
                             productData.is_available : true
    };

  // Load variants properly with the correct structure
    if (productData.variants && Array.isArray(productData.variants)) {
      variants.value = productData.variants.map(variant => ({
        variant_name: variant.variant_name || '',
        options: Array.isArray(variant.options) 
          ? variant.options.map(option => ({
              name: option.name || '',
              price: option.price !== undefined && option.price !== null
                ? Number(option.price)
                : 0,
              stock: option.stock || 0,
              discount: option.discount !== undefined && option.discount !== null ? Number(option.discount) : 0,
              sku: option.sku || '',
              distributor_price: option.distributor_price !== undefined && option.distributor_price !== null ? Number(option.distributor_price) : null,
              weight: option.weight ?? null,
              weight_input: option.weight != null ? Number(option.weight) : null,
              weight_unit: 'gr',
              length: option.length != null ? Number(option.length) : null,
              width: option.width != null ? Number(option.width) : null,
              height: option.height != null ? Number(option.height) : null
            }))
          : []
      }));
      console.log('Loaded variants:', variants.value);
    } else {
      variants.value = [];
    }

    await loadAttributeDefinitions(product.value.product_type);

    console.log('Product loaded successfully:', product.value);
    
    // Initialize rich text editor with product description
    nextTick(() => {
      initDescriptionEditor();
    });
  } catch (error) {
    console.error('Error loading product:', error);
    toast.error(error.message || 'Error al cargar el producto');
    router.push('/configuracion/productos');
  } finally {
    isSubmitting.value = false;
  }
};

// Añadir watcher para productId
watch(() => route.params.productId, async (newId) => {
  if (newId) {
    await loadProductData(newId);
  } else {
    // Reset form if no ID
    resetForm();
    isEditMode.value = false;
  }
});

onMounted(async () => {
  try {
    // Obtener la tasa de dólar actual desde el backend
    const dollarRateObj = await getCurrentDollarRate();
    if (dollarRateObj && dollarRateObj.value) {
      currentDollarRate.value = parseFloat(dollarRateObj.value);
      if (dollarRateObj.update_date) {
        dollarUpdateDate.value = new Date(dollarRateObj.update_date).toLocaleDateString();
      }
    } else {
      // Rate not available — leave as null, conversion indicators will show warning
      currentDollarRate.value = null;
      dollarUpdateDate.value = null;
    }
    
    // Load categories first
    const categoriesResponse = await getCategories();
    categories.value = categoriesResponse;
    console.log('Categories loaded:', categories.value);

    // Load product data if in edit mode
    if (productId) {
      await loadProductData(productId, true);
    } else {
      await loadAttributeDefinitions(product.value.product_type);
    }

    // Initialize modal after the DOM is updated
    await nextTick();
    const modalElement = document.getElementById('deleteCategoryModal');
    if (modalElement) {
      modalInstance = new bootstrap.Modal(modalElement);
    }
    const productTypeModalElement = document.getElementById('chooseProductTypeModal');
    if (productTypeModalElement) {
      productTypeModalInstance = new bootstrap.Modal(productTypeModalElement);
    }
  } catch (error) {
    console.error('Error in component initialization:', error);
    toast.error('Error al cargar las categorías');
  }
});

onUnmounted(() => {
  revokeIfBlobUrl(imagePreview.value);
  revokeIfBlobUrl(imagePreview2.value);
  revokeIfBlobUrl(imagePreview3.value);
  revokeIfBlobUrl(imagePreview4.value);
});

// Computed property para calcular el precio en USD
const calculatePriceInUSD = computed(() => {
  if (!product.value.price || parseFloat(product.value.price) <= 0) {
    return null;
  }
  if (product.value.currency === 'USD') {
    return parseFloat(product.value.price).toFixed(2);
  }
  if (!currentDollarRate.value || parseFloat(currentDollarRate.value) <= 0) {
    return null;
  }
  const priceInUSD = parseFloat(product.value.price) / parseFloat(currentDollarRate.value);
  return priceInUSD.toFixed(2);
});

/** Vista previa en g según unidad (misma lógica que al enviar) */
const productWeightInGramsPreview = computed(() => {
  const w = toGrams(product.value.weight_input, product.value.weight_unit || 'gr');
  return w == null ? null : w;
});

const handleUploadImage = (event, imageField) => {
  const file = event.target.files?.[0];
  if (!file) return;
  try {
    if (imageField === 'image') {
      revokeIfBlobUrl(imagePreview.value);
      pendingImageFile.value = file;
      imagePreview.value = URL.createObjectURL(file);
    } else if (imageField === 'image_2') {
      revokeIfBlobUrl(imagePreview2.value);
      pendingImage2File.value = file;
      imagePreview2.value = URL.createObjectURL(file);
    } else if (imageField === 'image_3') {
      revokeIfBlobUrl(imagePreview3.value);
      pendingImage3File.value = file;
      imagePreview3.value = URL.createObjectURL(file);
    } else if (imageField === 'image_4') {
      revokeIfBlobUrl(imagePreview4.value);
      pendingImage4File.value = file;
      imagePreview4.value = URL.createObjectURL(file);
    }
  } catch (error) {
    console.error('Error preparing image:', error);
    toast.error('No se pudo cargar la imagen');
  }
};

const handlePaste = (event) => {
  const items = event.clipboardData.items;
  for (let i = 0; i < items.length; i++) {
    if (items[i].type.indexOf("image") !== -1) {
      const file = items[i].getAsFile();
      handleUploadImage({ target: { files: [file] } }, 'image');
    }
  }
};

const handleSubmit = async () => {
  try {
    if (!product.value.name?.trim()) {
      toast.error('El nombre del producto es requerido');
      return;
    }

    if (!product.value.category) {
      toast.error('Por favor selecciona una categoría');
      return;
    }

    if (!product.value.product_type) {
      toast.error('Defini el tipo de producto antes de guardar');
      return;
    }

    // Validar currency antes de enviar
    if (!product.value.currency || !['ARS', 'USD'].includes(product.value.currency)) {
      toast.error('La moneda es requerida y debe ser ARS o USD');
      return;
    }

  // Validate variants
    const validVariants = variants.value
      .filter(v => v.variant_name && v.variant_name.trim() && v.options && v.options.length > 0)
      .map(v => ({
        variant_name: v.variant_name.trim(),
        options: v.options
          .filter(o => o.name && o.name.trim())
          .map(o => {
            const w = toGrams(o.weight_input ?? o.weight, o.weight_unit || 'gr');
            return {
              name: o.name.trim(),
              price: o.price !== undefined && o.price !== null ? Number(o.price) : undefined,
              stock: Number(o.stock || 0),
              discount: o.discount !== undefined && o.discount !== null ? Number(o.discount) : 0,
              sku: o.sku ? o.sku.trim() : undefined,
              distributor_price: o.distributor_price !== undefined && o.distributor_price !== null ? Number(o.distributor_price) : undefined,
              weight: w,
              length: o.length != null && o.length !== '' ? Number(o.length) : undefined,
              width: o.width != null && o.width !== '' ? Number(o.width) : undefined,
              height: o.height != null && o.height !== '' ? Number(o.height) : undefined
            };
          })
      }));
      
    const hasValidVariants = validVariants.length > 0 && 
                           validVariants.every(v => v.options && v.options.length > 0);

    const selectedAttributes = {};
    for (const key of selectedAttributeKeys.value) {
      if (!(key in (product.value.attributes || {}))) continue;
      const value = product.value.attributes[key];
      if (value === null || value === undefined || value === '') continue;
      selectedAttributes[key] = value;
    }
    
    const productToSend = {
      name: product.value.name.trim(),
      brand: (product.value.brand || '').trim(),
      sku: (product.value.sku || '').trim() || undefined,
      description: (product.value.description || '').trim(),
      category: parseInt(product.value.category),
      price: Number(product.value.price || 0),
      discount: Number(product.value.discount || 0),
      distributor_price: product.value.distributor_price ? Number(product.value.distributor_price) : null,
      is_restricted: product.value.is_restricted || false,
      stock: Number(product.value.stock || 0),
      volume: (product.value.volume || '').trim(),
      dimensions: (product.value.dimensions || '').trim(),
      weight: toGrams(product.value.weight_input ?? product.value.weight, product.value.weight_unit || 'gr'),
      length: product.value.length != null && product.value.length !== '' ? Number(product.value.length) : null,
      width: product.value.width != null && product.value.width !== '' ? Number(product.value.width) : null,
      height: product.value.height != null && product.value.height !== '' ? Number(product.value.height) : null,
      image: product.value.image,
      image_2: product.value.image_2,
      image_3: product.value.image_3,
      image_4: product.value.image_4,
      is_available: product.value.is_available,
      has_variants: hasValidVariants,
      variants: validVariants,
      currency: product.value.currency,
      has_special_promotion: product.value.has_special_promotion || false,
      special_promotion_name: (product.value.special_promotion_name || '').trim(),
      product_type: product.value.product_type,
      attributes: selectedAttributes,
    };

    console.log('Sending variants data:', validVariants);
    console.log('Sending product data:', productToSend);
    isSubmitting.value = true;

    const imageFiles = {
      imageFile: pendingImageFile.value || undefined,
      image2File: pendingImage2File.value || undefined,
      image3File: pendingImage3File.value || undefined,
      image4File: pendingImage4File.value || undefined,
    };

    if (isEditMode.value) {
      const saved = await updateProduct(productId, productToSend, imageFiles);
      toast.success("Producto actualizado exitosamente");
      pendingImageFile.value = null;
      pendingImage2File.value = null;
      pendingImage3File.value = null;
      pendingImage4File.value = null;
      applyImageFieldsFromApi(saved);
      // force=true: sin caché del navegador/CDN en el JSON del producto (evita volver a la imagen anterior)
      await loadProductData(productId, true);
    } else {
      const newProduct = await createProduct(productToSend, imageFiles);
      toast.success("Producto creado exitosamente");
      pendingImageFile.value = null;
      pendingImage2File.value = null;
      pendingImage3File.value = null;
      pendingImage4File.value = null;
      applyImageFieldsFromApi(newProduct);
      if (newProduct && newProduct.id) {
        isEditMode.value = true;
        await router.replace(`/configuracion/productos/editar/${newProduct.id}`);
        await loadProductData(newProduct.id, true);
      }
    }
  } catch (error) {
    console.error('Error saving product:', error);
    toast.error(error.response?.data?.message || error.message || "Error al guardar el producto");
  } finally {
    isSubmitting.value = false;
  }
};

const removeImage = (imageField) => {
  product.value[imageField] = null;
  
  if (imageField === 'image') {
    revokeIfBlobUrl(imagePreview.value);
    imagePreview.value = null;
    pendingImageFile.value = null;
    if (imageUrlInput.value) {
      imageUrlInput.value.value = "";
    }
  } else if (imageField === 'image_2') {
    revokeIfBlobUrl(imagePreview2.value);
    imagePreview2.value = null;
    pendingImage2File.value = null;
    if (imageUrlInput2.value) {
      imageUrlInput2.value.value = "";
    }
  } else if (imageField === 'image_3') {
    revokeIfBlobUrl(imagePreview3.value);
    imagePreview3.value = null;
    pendingImage3File.value = null;
    if (imageUrlInput3.value) {
      imageUrlInput3.value.value = "";
    }
  } else if (imageField === 'image_4') {
    revokeIfBlobUrl(imagePreview4.value);
    imagePreview4.value = null;
    pendingImage4File.value = null;
    if (imageUrlInput4.value) {
      imageUrlInput4.value.value = "";
    }
  }
};

const resetForm = () => {
  product.value = { 
    ...initialProduct 
  };
  revokeIfBlobUrl(imagePreview.value);
  revokeIfBlobUrl(imagePreview2.value);
  revokeIfBlobUrl(imagePreview3.value);
  revokeIfBlobUrl(imagePreview4.value);
  imagePreview.value = null;
  imagePreview2.value = null;
  imagePreview3.value = null;
  imagePreview4.value = null;
  pendingImageFile.value = null;
  pendingImage2File.value = null;
  pendingImage3File.value = null;
  pendingImage4File.value = null;
  
  if (imageUrlInput.value) imageUrlInput.value.value = "";
  if (imageUrlInput2.value) imageUrlInput2.value.value = "";
  if (imageUrlInput3.value) imageUrlInput3.value.value = "";
  if (imageUrlInput4.value) imageUrlInput4.value.value = "";
  
  variants.value = [];
  attributeDefinitions.value = [];
  selectedAttributeKeys.value = [];
  attributeKeyToAdd.value = '';
};
</script>

<style scoped>
/* ===== Container ===== */
.product-form-container {
  padding: 0 1rem 5rem;
  overflow: visible;
}

/* ===== Header: siempre visible al hacer scroll del documento ===== */
.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.85rem 0;
  margin: 0 -0.25rem 1rem;
  padding-left: max(0.25rem, env(safe-area-inset-left, 0px));
  padding-right: max(0.25rem, env(safe-area-inset-right, 0px));
  position: sticky;
  /* Offset definido en AdminPanel (0 desktop; móvil = altura toolbar + safe area) */
  top: var(--admin-sticky-offset, 0px);
  z-index: 1025;
  background: rgba(20, 20, 20, 0.94);
  background: color-mix(in srgb, var(--black-color-2, #141414) 94%, transparent);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
}

@supports not (backdrop-filter: blur(1px)) {
  .form-header {
    background: var(--black-color-2, #141414);
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
}

.header-left > div:last-child {
  min-width: 0;
}

.back-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255,255,255,0.06);
  color: var(--white-color-2, #f5f5f5);
  text-decoration: none;
  transition: all 0.15s ease;
}
.back-link:hover {
  background: rgba(255,255,255,0.12);
  color: #fff;
}

.form-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.form-subtitle {
  font-size: 0.78rem;
  color: #999;
  margin: 2px 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

/* ===== Buttons ===== */
.btn-action {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.84rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.btn-action-primary {
  background: var(--primary-color);
  color: #fff;
}
.btn-action-primary:hover:not(:disabled) {
  filter: brightness(1.15);
}

.option-label{
  color: var(--white-color-2);
}
.btn-action-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn-action-secondary {
  background: rgba(255,255,255,0.07);
  color: #ccc;
}
.btn-action-secondary:hover {
  background: rgba(255,255,255,0.13);
  color: #fff;
}
.btn-action-danger {
  background: rgba(239,68,68,0.15);
  color: #f87171;
}
.btn-action-danger:hover:not(:disabled) {
  background: rgba(239,68,68,0.25);
}
.btn-action-sm { padding: 0.35rem 0.75rem; font-size: 0.78rem; }
.btn-action-full { width: 100%; justify-content: center; padding: 0.7rem; }

/* ===== Section layout (timeline) ===== */
.form-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 0;
}

.section-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding-top: 2px;
}

.indicator-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  background: rgba(var(--primary-color-rgb), 0.15);
  color: var(--primary-light);
}
.indicator-icon--pricing { background: rgba(59,130,246,0.12); color: #60a5fa; }
.indicator-icon--promo   { background: rgba(245,158,11,0.12); color: #fbbf24; }
.indicator-icon--variants{ background: rgba(168,85,247,0.12); color: #c084fc; }
.indicator-icon--images  { background: rgba(236,72,153,0.12); color: #f472b6; }
.indicator-icon--specs   { background: rgba(220,38,38,0.16); color: #fda4a4; }

.indicator-line {
  width: 2px;
  flex: 1;
  background: rgba(255,255,255,0.06);
  margin: 0.4rem 0;
  border-radius: 1px;
}

.section-content {
  flex: 1;
  min-width: 0;
  padding-bottom: 1.25rem;
}

.section-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #999;
  margin-bottom: 0.6rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

/* ===== Form card ===== */
.form-card {
  background: var(--black-color-3, #1c1c1c);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 1.25rem;
}
.form-card--compact {
  padding: 1rem 1.25rem;
}

.details-equal-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* ===== Fields ===== */
.field-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  color: #bbb;
  margin-bottom: 0.3rem;
}

.required-dot {
  display: inline-block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--primary-light);
  vertical-align: middle;
  margin-left: 3px;
}

.field-input {
  display: block;
  width: 100%;
  padding: 0.5rem 0.7rem;
  font-size: 0.87rem;
  color: #fff;
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.field-input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2);
}
.field-input::placeholder { color: #555; }
.field-input--sm { padding: 0.4rem 0.6rem; font-size: 0.8rem; }
.field-input--price {
  border: none;
  background: transparent;
  border-radius: 0;
}
.field-input--price:focus { box-shadow: none; }

select.field-input { cursor: pointer; }
select.field-input option {
  background: var(--black-color-3, #1c1c1c);
  color: #fff;
}

.field-hint {
  display: block;
  font-size: 0.72rem;
  color: #666;
  margin-top: 0.2rem;
}

.product-type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.55rem;
}

.product-type-modal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.65rem;
}

.tech-type-required {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.65rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: rgba(245,158,11,0.1);
  color: #fcd34d;
  font-size: 0.8rem;
  font-weight: 600;
}

.product-type-card {
  min-height: 92px;
  padding: 0.75rem;
  border: 1px solid rgba(255,255,255,0.09);
  border-radius: 8px;
  background: rgba(0,0,0,0.22);
  color: #c7c7c7;
  text-align: left;
  display: grid;
  grid-template-columns: 28px 1fr;
  grid-template-rows: auto auto;
  gap: 0.15rem 0.55rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
}

.product-type-card i {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fecaca;
  background: rgba(220, 38, 38, 0.18);
  grid-row: 1 / span 2;
}

.product-type-card span {
  color: #f4f4f4;
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.2;
}

.product-type-card small {
  color: #777;
  font-size: 0.7rem;
  line-height: 1.25;
}

.product-type-card:hover,
.product-type-card--active {
  border-color: rgba(248,113,113,0.65);
  background: rgba(127, 29, 29, 0.28);
  transform: translateY(-1px);
}

.product-type-card--modal {
  min-height: 98px;
}

.tech-sheet-builder {
  border: 1px solid rgba(248,113,113,0.24);
  border-radius: 10px;
  background: rgba(0,0,0,0.18);
  overflow: hidden;
}

.tech-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  background: rgba(255,255,255,0.03);
  flex-wrap: wrap;
}

.tech-sheet-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.add-spec-control {
  display: flex;
  align-items: stretch;
  gap: 0.4rem;
  min-width: min(100%, 390px);
}

.add-spec-control .field-input {
  min-width: 190px;
}

.tech-sheet-empty {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 1rem;
  color: #888;
  font-size: 0.82rem;
}

.tech-sheet-empty i {
  color: #fca5a5;
}

.tech-sheet-title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.tech-sheet-title > i {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fecaca;
  background: rgba(220, 38, 38, 0.2);
}

.tech-sheet-title strong {
  display: block;
  color: #fff;
  font-size: 0.95rem;
}

.tech-sheet-title span {
  display: block;
  color: #777;
  font-size: 0.73rem;
  margin-top: 0.05rem;
}

.attribute-group {
  padding: 1rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}

.attribute-group:last-child {
  border-bottom: none;
}

.attribute-group-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.65rem;
  color: #aaa;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.attribute-group-title i {
  color: #fca5a5;
}

.attribute-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.attribute-field {
  position: relative;
  min-width: 0;
}

.attribute-field--highlight .field-input {
  border-color: rgba(248,113,113,0.4);
}

.attribute-label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding-right: 1.75rem;
}

.attribute-label i {
  width: 18px;
  color: #888;
  text-align: center;
}

.attribute-toggle {
  min-height: 39px;
  padding: 0.5rem 0;
}

.attribute-remove {
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #666;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.attribute-remove:hover {
  background: rgba(239,68,68,0.12);
  color: #f87171;
}

.btn-red {
  background: #b91c1c !important;
}

.btn-red:hover:not(:disabled) {
  background: #dc2626 !important;
}

/* ===== Field group (input + buttons) ===== */
.field-group {
  display: flex;
}
.field-group .field-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  flex: 1;
  min-width: 0;
}
.field-group-actions {
  display: flex;
}
.field-group-actions .icon-btn {
  border-radius: 0;
  border-left: none;
}
.field-group-actions .icon-btn:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}
.field-group--sm .field-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.field-group--sm .icon-btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
.field-group--shipping {
  display: flex;
  gap: 0.25rem;
}
.field-group--shipping .field-input { flex: 1; min-width: 60px; }
.field-input--unit { flex: 0 0 70px !important; min-width: 70px !important; }
.shipping-section-label {
  font-size: 0.8rem;
  color: #999;
  margin-bottom: 0.5rem;
}
.option-shipping-row {
  display: flex;
  gap: 0.25rem;
  align-items: center;
  flex-wrap: wrap;
}
.option-field--shipping { min-width: 280px; }

/* ===== Icon buttons ===== */
.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  min-width: 36px;
  border: 1px solid rgba(255,255,255,0.1);
  background: rgba(255,255,255,0.04);
  color: #999;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.8rem;
}
.icon-btn:hover { background: rgba(255,255,255,0.1); color: #fff; }
.icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.icon-btn--sm { width: 30px; min-width: 30px; font-size: 0.72rem; }
.icon-btn-brand { color: var(--primary-light); border-color: rgba(var(--primary-color-rgb), 0.25); }
.icon-btn-brand:hover { background: rgba(var(--primary-color-rgb), 0.2); }
.icon-btn-danger { color: #f87171; border-color: rgba(239,68,68,0.2); }
.icon-btn-danger:hover { background: rgba(239,68,68,0.15); }

/* ===== Price input ===== */
.price-input {
  display: flex;
  align-items: stretch;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.1);
  background: rgba(0,0,0,0.3);
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.price-input:focus-within {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2);
}
.price-currency {
  display: flex;
  align-items: center;
  padding: 0 0.6rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #777;
  background: rgba(255,255,255,0.03);
  user-select: none;
  white-space: nowrap;
}
.price-currency--right { background: transparent; }
.price-input--sm { border-radius: 6px; }
.price-input--sm .price-currency { padding: 0 0.45rem; font-size: 0.72rem; }
.price-input--sm .field-input--price { padding: 0.4rem 0.5rem; font-size: 0.8rem; }

/* ===== Conversion badge ===== */
.conversion-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
  background: rgba(59,130,246,0.1);
  color: #93c5fd;
  margin-top: 0.25rem;
}
.conversion-badge--warn {
  background: rgba(245,158,11,0.1);
  color: #fcd34d;
}

/* ===== Toggle switch ===== */
.toggle-field {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}
.toggle-input { display: none; }
.toggle-switch {
  position: relative;
  width: 34px;
  height: 18px;
  border-radius: 9px;
  background: rgba(255,255,255,0.12);
  transition: background 0.2s ease;
  flex-shrink: 0;
}
.toggle-switch::after {
  content: '';
  position: absolute;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #888;
  top: 2px;
  left: 2px;
  transition: transform 0.2s ease, background 0.2s ease;
}
.toggle-input:checked + .toggle-switch { background: var(--primary-color); }
.toggle-input:checked + .toggle-switch::after { transform: translateX(16px); background: #fff; }
.toggle-label { font-size: 0.82rem; color: #ccc; }

/* ===== Divider ===== */
.divider {
  height: 1px;
  background: rgba(255,255,255,0.06);
  margin: 0.15rem 0;
}

/* ===== Rich text editor ===== */
.rich-editor {
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  overflow: hidden;
}
.editor-toolbar {
  background: rgba(0,0,0,0.25);
  padding: 0.4rem;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  display: flex;
  align-items: center;
  gap: 2px;
  flex-wrap: wrap;
}
.toolbar-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #888;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.78rem;
  transition: all 0.15s ease;
}
.toolbar-btn:hover { background: rgba(255,255,255,0.08); color: #fff; }
.toolbar-divider {
  width: 1px;
  height: 16px;
  background: rgba(255,255,255,0.1);
  margin: 0 3px;
}
.editor-content {
  min-height: 130px;
  max-height: 350px;
  overflow-y: auto;
  padding: 0.75rem 1rem;
  background: rgba(0,0,0,0.2);
  color: #fff;
  border: none;
  outline: none;
  font-size: 0.87rem;
  line-height: 1.6;
}
.editor-content:empty::before {
  content: attr(placeholder);
  color: #555;
  pointer-events: none;
}
.editor-content:focus { background: rgba(0,0,0,0.28); }
.editor-content p { margin-bottom: 0.6rem; }
.editor-content ul, .editor-content ol { padding-left: 1.25rem; margin-bottom: 0.6rem; }
.editor-content li { margin-bottom: 0.15rem; }
.editor-content a { color: var(--primary-light); }
.editor-content strong { color: #fff; }
.editor-content h3 { font-size: 1rem; font-weight: 700; color: #fff; margin: 0.5rem 0 0.3rem; }
.editor-content h3:first-child { margin-top: 0; }

/* ===== Empty state ===== */
.empty-state {
  text-align: center;
  padding: 1.75rem 1rem;
  background: var(--black-color-3, #1c1c1c);
  border-radius: 12px;
  border: 1px dashed rgba(255,255,255,0.1);
}
.empty-state-icon { font-size: 1.4rem; color: #444; margin-bottom: 0.4rem; }
.empty-state p { font-size: 0.83rem; color: #666; margin-bottom: 0.6rem; }

/* ===== Variants ===== */
.variant-block {
  background: var(--black-color-3, #1c1c1c);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  margin-bottom: 0.6rem;
  overflow: hidden;
}
.variant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.8rem;
  background: rgba(0,0,0,0.15);
  gap: 0.6rem;
}
.variant-header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}
.variant-number {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(168,85,247,0.12);
  color: #c084fc;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
}
.variant-name-input {
  border: none !important;
  background: transparent !important;
  font-weight: 600;
  color: #fff;
  padding: 0.25rem 0.4rem;
  font-size: 0.88rem;
  box-shadow: none !important;
}
.variant-name-input:focus {
  background: rgba(0,0,0,0.2) !important;
  border-radius: 6px;
}
.variant-header-actions { display: flex; gap: 0.3rem; }

.variant-options-list { padding: 0.4rem 0.7rem 0.7rem; }
.variant-options-empty {
  text-align: center;
  padding: 0.6rem;
  color: #555;
  font-size: 0.78rem;
}

.option-row {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  padding: 0.5rem 0.3rem;
  border-radius: 8px;
  margin-bottom: 0.25rem;
  transition: background 0.15s ease;
}
.option-row:hover { background: rgba(255,255,255,0.025); }

.option-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  flex: 1;
  min-width: 0;
}
.option-field { min-width: 0; }
.option-field--name   { flex: 2; min-width: 110px; }
.option-field--price  { flex: 1.5; min-width: 95px; }
.option-field--stock  { flex: 0.7; min-width: 55px; }
.option-field--sku    { flex: 1.5; min-width: 95px; }
.option-field--discount{ flex: 0.8; min-width: 70px; }
.option-field--distrib{ flex: 1.2; min-width: 85px; }

.subsection-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #888;
}

.option-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #555;
  cursor: pointer;
  font-size: 0.72rem;
  transition: all 0.15s ease;
  margin-top: 1rem;
  flex-shrink: 0;
}
.option-remove:hover { background: rgba(239,68,68,0.12); color: #f87171; }

.add-variant-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.55rem;
  border-radius: 5px;
  font-size: 0.7rem;
  font-weight: 600;
  border: 1px solid rgba(168,85,247,0.25);
  background: rgba(168,85,247,0.08);
  color: #c084fc;
  cursor: pointer;
  transition: all 0.15s ease;
}
.add-variant-btn:hover { background: rgba(168,85,247,0.16); }

/* ===== Images ===== */
.images-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.image-slot-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #666;
  margin-bottom: 0.4rem;
}
.image-upload-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 1.75rem 1rem;
  border: 2px dashed rgba(255,255,255,0.1);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #666;
  text-align: center;
}
.image-upload-zone:hover {
  border-color: rgba(var(--primary-color-rgb), 0.35);
  background: rgba(var(--primary-color-rgb), 0.04);
  color: #aaa;
}
.image-upload-zone i { font-size: 1.3rem; }
.image-upload-zone span { font-size: 0.8rem; font-weight: 500; }
.image-upload-zone small { font-size: 0.68rem; color: #555; }

.image-preview-card {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.08);
  aspect-ratio: 1;
  background: rgba(0,0,0,0.2);
}
.image-preview-card img { width: 100%; height: 100%; object-fit: contain; }
.image-remove-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  background: rgba(0,0,0,0.6);
  color: #f87171;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.72rem;
  opacity: 0;
  transition: opacity 0.15s ease;
}
.image-preview-card:hover .image-remove-btn { opacity: 1; }
.image-remove-btn:hover { background: rgba(239,68,68,0.8); color: #fff; }

/* ===== Floating save (mobile) ===== */
.floating-save { display: none; }

/* ===== Modal ===== */
.modal-dark {
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.1);
  color: #ccc;
  border-radius: 12px;
  overflow: hidden;
}
.modal-dark .modal-header { border-bottom: 1px solid rgba(255,255,255,0.07); padding: 1rem 1.25rem; }
.modal-dark .modal-body { padding: 1.25rem; }
.modal-dark .modal-footer { border-top: 1px solid rgba(255,255,255,0.07); padding: 0.75rem 1.25rem; }
.modal-dark .modal-title { font-size: 1rem; font-weight: 600; }

/* ===== Transitions ===== */
.slide-fade-enter-active { transition: all 0.2s ease; }
.slide-fade-leave-active { transition: all 0.15s ease; }
.slide-fade-enter-from, .slide-fade-leave-to { opacity: 0; transform: translateX(-8px); }

.option-list-enter-active { transition: all 0.2s ease; }
.option-list-leave-active { transition: all 0.15s ease; }
.option-list-enter-from { opacity: 0; transform: translateY(-6px); }
.option-list-leave-to { opacity: 0; transform: translateX(15px); }

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .product-form-container { padding: 0 0.5rem 4.5rem; }
  .form-title { font-size: 1.05rem; }
  .section-indicator { display: none; }
  .form-section { gap: 0; }
  .section-content { padding-bottom: 0.75rem; }
  .form-card { padding: 1rem; border-radius: 10px; }
  .option-fields { flex-direction: column; gap: 0.35rem; }
  .option-field { min-width: 100% !important; flex: 1 1 100% !important; }
  .product-type-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .attribute-grid { grid-template-columns: 1fr; }
  .add-spec-control { width: 100%; flex-direction: column; }
  .tech-sheet-actions { width: 100%; }
  .option-remove { margin-top: 0; align-self: flex-end; }
  .images-grid { grid-template-columns: 1fr; }
  .variant-header { flex-wrap: wrap; }
  .floating-save {
    display: block;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0.65rem 1rem;
    background: rgba(20,20,20,0.95);
    backdrop-filter: blur(8px);
    border-top: 1px solid rgba(255,255,255,0.07);
    z-index: 20;
  }
  .header-actions .btn-action-primary { display: none; }
}

@media (max-width: 480px) {
  .header-actions .btn-action-secondary span { display: none; }
  .product-type-grid { grid-template-columns: 1fr; }
}
</style>
